import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Logo, Brand } from "@/components/Logo";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { ARTICLES } from "@/data/articles";
import { api } from "@/lib/api";

const fmt = (d) => new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

export default function Articles() {
  const navigate = useNavigate();
  const [items, setItems] = useState(ARTICLES);

  useEffect(() => {
    api.get("/articles").then((r) => {
      const db = (r.data || []).map((a) => ({ ...a, _db: true }));
      const staticSlugs = new Set(db.map((a) => a.slug));
      setItems([...db, ...ARTICLES.filter((a) => !staticSlugs.has(a.slug))]);
    }).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="articles-page">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-[#2a3040]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="articles-back" onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors">
            <ArrowLeft size={18} /> Home
          </button>
          <Logo size="text-xl" />
          <button onClick={() => navigate("/auth")}
            className="text-sm font-bold text-[#FF5A00] hover:text-[#FF7A33] transition-colors">Sign in</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-12">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF5A00] font-bold mb-3">
          <BookOpen size={14} /> Articles & Guides
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-3 text-white">
          Knowledge for the culture
        </h1>
        <p className="text-[#9ca3af] max-w-2xl mb-10">
          Original guides on wealth, business, health, history, Politics and technology — free to read, written with
          Black-community context front and center by <Brand /> members.
          <br />
          Views and Opinions are those of the Writers, Not the Views and Opinions of <Brand />.
        </p>

        <div className="grid sm:grid-cols-2 gap-5">
          {items.map((a, i) => (
            <motion.button key={a.slug} data-testid={`article-card-${a.slug}`}
              onClick={() => navigate(`/articles/${a.slug}`)}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="text-left bg-[#15151d] border border-[#2a3040] rounded-2xl p-6 hover:border-[#FF5A00]/50 transition-colors group">
              <div className="text-[11px] uppercase tracking-wider text-[#FF5A00] font-bold mb-2">{a.category}</div>
              <h2 className="font-display font-bold text-xl text-white mb-2 group-hover:text-[#FF5A00] transition-colors">{a.title}</h2>
              <p className="text-sm text-[#9ca3af] leading-relaxed mb-4">{a.excerpt}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#6b7280]">{fmt(a.date)}</span>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-[#FF5A00]">Read <ArrowRight size={15} /></span>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-14 text-center bg-[#15151d] border border-[#2a3040] rounded-2xl p-8">
          <h3 className="font-display font-black text-2xl text-white mb-2">Get the full <Brand /> experience</h3>
          <p className="text-[#9ca3af] mb-5 max-w-xl mx-auto">Chat with an AI that knows your context, real-time news, community, and daily tools.</p>
          <button data-testid="articles-cta" onClick={() => navigate("/auth")}
            className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-6 py-3 hover:bg-[#FF7A33] transition-colors">
            Get Started Free <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
